import { Fragment, useEffect, useRef, useState } from "react";
import { ArrowUp, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, getToken } from "@/lib/api";

const SUGGESTIONS = [
  "Quanto costa ristrutturare un bagno?",
  "A che punto è il mio cantiere?",
  "Cos'è la CILA e chi la gestisce?",
  "Quali sono le fasi del chiavi in mano?",
];

export const renderMd = (text) =>
  text.split("\n").map((line, i, arr) => (
    <Fragment key={i}>
      {line.split(/\*\*(.+?)\*\*/g).map((part, j) => (j % 2 ? <strong key={j} className="font-semibold">{part}</strong> : part))}
      {i < arr.length - 1 && <br />}
    </Fragment>
  ));

async function streamChat(text, onDelta) {
  const res = await fetch(`${BACKEND}/api/ai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Errore dell'assistente");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (!line.startsWith("data:")) continue;
      const ev = JSON.parse(line.slice(5));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
    }
  }
}

export const AiChat = () => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef(null);

  useEffect(() => { api.get("/ai/history").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [items]);

  const ask = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText("");
    setBusy(true);
    setItems((it) => [...it, { id: `u${Date.now()}`, role: "user", content: question }, { id: "stream", role: "assistant", content: "" }]);
    try {
      await streamChat(question, (d) => setItems((it) => it.map((m) => (m.id === "stream" ? { ...m, content: m.content + d } : m))));
    } catch (e) {
      toast.error(e.message);
      setItems((it) => it.filter((m) => m.id !== "stream" || m.content));
    } finally {
      setItems((it) => it.map((m) => (m.id === "stream" ? { ...m, id: `a${Date.now()}` } : m)));
      setBusy(false);
    }
  };

  const clear = async () => {
    await api.delete("/ai/history");
    setItems([]);
  };

  return (
    <div className="rp-card flex flex-col h-[62vh] overflow-hidden" data-testid="ai-chat">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#EBEBEB] bg-white">
        <p className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-[#C8BDAB]" /> Assistente AI RP Home</p>
        {items.length > 0 && (
          <button onClick={clear} disabled={busy} data-testid="ai-clear-history" className="flex items-center gap-1.5 text-xs text-[#757575] hover:text-black"><Trash2 className="h-3.5 w-3.5" /> Cancella</button>
        )}
      </div>
      <div ref={box} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
        {!items.length && (
          <div className="h-full flex flex-col justify-center gap-5 max-w-md mx-auto text-center">
            <div>
              <p className="text-xl font-semibold tracking-tight">Ciao! Come posso aiutarti?</p>
              <p className="text-sm text-[#757575] mt-1">Costi indicativi, fasi dei lavori, pratiche e stato del tuo cantiere.</p>
            </div>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => ask(s)} data-testid={`ai-suggestion-${i}`} className="rounded-2xl bg-white border border-[#EBEBEB] px-4 py-3 text-sm text-left hover:bg-[#E6DDCE] transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {items.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}`}>
            <div className={`max-w-[85%] rounded-[20px] px-4 py-3 text-[15px] leading-relaxed ${m.role === "user" ? "bg-[#070707] text-white rounded-br-md" : "bg-[#E6DDCE] rounded-bl-md"}`}>
              {m.content ? renderMd(m.content) : (
                <span className="flex gap-1 py-1"><span className="typing-dot h-1.5 w-1.5 rounded-full bg-black" /><span className="typing-dot h-1.5 w-1.5 rounded-full bg-black" /><span className="typing-dot h-1.5 w-1.5 rounded-full bg-black" /></span>
              )}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-[#EBEBEB] bg-white flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Fai una domanda all'assistente…" className="rp-field rounded-full" data-testid="ai-chat-input" />
        <button disabled={busy || !text.trim()} data-testid="ai-chat-send-button" className="h-12 w-12 shrink-0 rounded-full bg-[#070707] text-white flex items-center justify-center disabled:opacity-40">
          <ArrowUp className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};
